import React from 'react';
import { X, Bell, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { format, addMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useThemeStore } from '../stores/themeStore';
import { Appointment } from './CalendarView';
import { useAgenciesStore } from '../stores/agenciesStore';

interface AppointmentFormProps {
  onSubmit: (data: any) => void;
  onClose: () => void;
  selectedDate?: Date | null;
  appointment?: Appointment;
  isEditing?: boolean;
  preselectedTime?: string;
  onDelete?: () => void;
}

function AppointmentForm({ onSubmit, onClose, selectedDate, appointment, isEditing = false, preselectedTime, onDelete }: AppointmentFormProps) {
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: isEditing && appointment ? {
      title: appointment.title,
      date: format(new Date(appointment.date), 'yyyy-MM-dd'),
      time: appointment.time,
      agency: appointment.agency,
      contact: appointment.contact,
      priority: appointment.priority,
      enableAlert: !!appointment.alertDate,
      alertTime: appointment.alertMinutes?.toString() || '15',
      alertType: appointment.alertType || 'email',
      suivi: appointment.suivi || ''
    } : {
      date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
      time: preselectedTime || '09:00',
      priority: 'normal',
      enableAlert: true,
      alertTime: '15',
      alertType: 'email',
      suivi: ''
    }
  });
  const { darkMode } = useThemeStore();
  const { agencies } = useAgenciesStore();

  const enableAlert = watch('enableAlert');

  const handleFormSubmit = (data: any) => {
    // Calculate alert date if enabled
    if (data.enableAlert) {
      const appointmentDate = new Date(`${data.date}T${data.time}`);
      const alertMinutes = parseInt(data.alertTime);
      const alertDate = addMinutes(appointmentDate, -alertMinutes);
      const now = new Date();

      // Vérifier si la date d'alerte est dans le passé
      if (alertDate <= now) {
        const confirmed = window.confirm(
          `⚠️ ATTENTION : La date d'alerte (${format(alertDate, 'dd/MM/yyyy à HH:mm', { locale: fr })}) est déjà passée.\n\n` +
          `L'email de rappel sera envoyé IMMÉDIATEMENT au lieu d'être envoyé ${alertMinutes} minutes AVANT le rendez-vous.\n\n` +
          `Voulez-vous continuer ?`
        );

        if (!confirmed) {
          return;
        }
      }

      data.alertDate = alertDate;
      data.alertMinutes = alertMinutes;
    }
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-2">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col`}>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col h-full">
          <div className="px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex justify-between items-center">
              <h2 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {isEditing ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
              </h2>
              <button type="button" onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 px-3 py-1.5">
            <div className="space-y-1.5">
              <div>
                <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                  Entreprise *
                </label>
                <input
                  type="text"
                  {...register("title", { required: "Ce champ est requis" })}
                  className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-sm`}
                />
                {errors.title && (
                  <span className="text-[10px] text-red-600">{errors.title.message}</span>
                )}
              </div>

              <div>
                <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                  Suivi *
                </label>
                <input
                  type="text"
                  {...register("suivi", { required: "Ce champ est requis" })}
                  className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-sm`}
                  placeholder="ex: Newsletter - Profils disponibles"
                />
                {errors.suivi && (
                  <span className="text-[10px] text-red-600">{errors.suivi.message}</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                    Date *
                  </label>
                  <input
                    type="date"
                    {...register("date", { required: "Ce champ est requis" })}
                    className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-sm`}
                  />
                  {errors.date && (
                    <span className="text-[10px] text-red-600">{errors.date.message}</span>
                  )}
                </div>
                <div>
                  <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                    Heure *
                  </label>
                  <input
                    type="time"
                    {...register("time", { required: "Ce champ est requis" })}
                    className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-sm`}
                  />
                  {errors.time && (
                    <span className="text-[10px] text-red-600">{errors.time.message}</span>
                  )}
                </div>
              </div>

              <div>
                <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                  Agence *
                </label>
                <select
                  {...register("agency", { required: "Ce champ est requis" })}
                  className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-sm`}
                >
                  <option value="">Sélectionner une agence</option>
                  {agencies.map(agency => (
                    <option key={agency.id} value={agency.name}>{agency.name}</option>
                  ))}
                </select>
                {errors.agency && (
                  <span className="text-[10px] text-red-600">{errors.agency.message}</span>
                )}
              </div>

              <div>
                <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                  Contact *
                </label>
                <input
                  type="text"
                  {...register("contact", { required: "Ce champ est requis" })}
                  className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-sm`}
                  placeholder="Nom du contact"
                />
                {errors.contact && (
                  <span className="text-[10px] text-red-600">{errors.contact.message}</span>
                )}
              </div>

              <div>
                <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                  Priorité
                </label>
                <select
                  {...register("priority")}
                  className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-sm`}
                >
                  <option value="high">Haute</option>
                  <option value="medium">Moyenne</option>
                  <option value="normal">Normale</option>
                </select>
              </div>

              <div className="space-y-1">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register("enableAlert")}
                    className={`h-3 w-3 text-anthea-blue rounded border-gray-300 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
                  />
                  <label className={`ml-1.5 text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} flex items-center`}>
                    <Bell className="w-3 h-3 mr-1" />
                    Activer une alerte
                  </label>
                </div>

                {enableAlert && (
                  <div className="pl-4 space-y-1">
                    <div>
                      <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                        Rappel
                      </label>
                      <select
                        {...register("alertTime")}
                        className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-xs`}
                      >
                        <option value="15">15 minutes</option>
                        <option value="30">30 minutes</option>
                        <option value="60">1 heure</option>
                        <option value="120">2 heures</option>
                        <option value="1440">1 jour</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-[11px] font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-0.5`}>
                        Type
                      </label>
                      <select
                        {...register("alertType")}
                        className={`w-full rounded-md border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200 text-gray-900'} p-1 text-xs`}
                      >
                        <option value="notification">Notification</option>
                        <option value="email">E-mail</option>
                        <option value="both">Notification et E-mail</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`px-3 py-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-b-lg flex justify-between gap-2 border-t border-gray-200 dark:border-gray-700 flex-shrink-0`}>
            {isEditing && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className={`flex items-center px-2 py-1 text-[11px] ${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-600'} rounded-lg hover:${darkMode ? 'bg-red-800' : 'bg-red-100'}`}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Supprimer
              </button>
            )}
            <div className="flex gap-1.5 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className={`px-2.5 py-1 text-[11px] ${darkMode ? 'text-gray-300 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'} rounded-lg transition-colors`}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-2.5 py-1 text-[11px] bg-gradient-anthea text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                {isEditing ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AppointmentForm;